import { billingTypeEnum, tripDutyTypeEnum } from 'utils/enumObj';
import {
  formatCurrency,
  formatNumber,
  getDateFormat,
  getTimeFormat,
  convertDecimalHoursToTime,
} from 'utils/utils';

// Helper function to calculate garage charges
const getGarageCharges = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const garageCharge = breakdown.find((item) => item?.chargeType === 'garageCharge') || {};
  const garageBreakup = garageCharge?.meta?.breakup || [];

  // totalCharge is the money; `rate` is the ₹/km tariff.
  const garageToPickup = parseFloat(garageBreakup[0]?.totalCharge || 0);
  const dropToGarage = parseFloat(garageBreakup[1]?.totalCharge || 0);

  return garageToPickup + dropToGarage;
};

// Overage charges live in the pricing-engine breakdown (chargeType extraKm /
// extraHrs); the legacy top-level fields are only populated by the old pricing
// path. Reading only the legacy fields dropped these charges from the
// document while the total (Payment.amount) still included them.
const getExtraKmCharge = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const fromBreakdown = breakdown.find(
    (b) => b?.chargeType?.toLowerCase() === 'extrakm',
  )?.addedCharge;
  if (fromBreakdown !== undefined && fromBreakdown !== null) return parseFloat(fromBreakdown) || 0;
  return tripDetails?.type === tripDutyTypeEnum?.rental
    ? parseFloat(tripDetails?.extraKmCharges || 0)
    : parseFloat(tripDetails?.extraPrice || 0);
};

const getExtraHrCharge = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const fromBreakdown = breakdown.find(
    (b) => b?.chargeType?.toLowerCase() === 'extrahrs',
  )?.addedCharge;
  if (fromBreakdown !== undefined && fromBreakdown !== null) return parseFloat(fromBreakdown) || 0;
  return parseFloat(tripDetails?.extraHrCharges || 0);
};

// Helper function to calculate Sub Total A (base fare + extra charges + surcharges + platform charge)
const calculateSubTotalA = (tripDetails) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const baseFare = tripDetails?.Payments?.[0]?.tripPricingDetails?.baseFareDisplay || 0;
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const extraKmCharges = getExtraKmCharge(tripDetails);
  const extraHrCharges = getExtraHrCharge(tripDetails);

  return baseFare + baseRate + surCharge + platformCharge + extraKmCharges + extraHrCharges;
};

// Helper function to calculate Sub Total B (all additional charges)
const calculateSubTotalB = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const driverAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'driverAllowance')?.addedCharge || 0,
  );
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'nightAllowance')?.addedCharge || 0,
  );
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'placard')?.addedCharge || 0,
  );
  const garageCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'garageCharge')?.addedCharge || 0,
  );
  const additionalCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'additionalCharges')?.addedCharge || 0,
  );
  const couponDiscount = Math.abs(
    parseFloat(breakdown.find((b) => b.chargeType === 'discountAmount')?.addedCharge || 0),
  );
  const tollCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'tollCharges')?.addedCharge || 0,
  );
  const parkingCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'parkingCharge')?.addedCharge || 0,
  );

  return (
    tollCharges +
    parkingCharges +
    driverAllowance +
    handlingCharges +
    nightAllowance +
    fuelSurcharge +
    interstateTax +
    placardCharge +
    garageCharge +
    additionalCharges -
    couponDiscount
  );
};

export const tripDutySlipData = (tripDetails, tripTypeDetails) => {
  // Add null check for tripDetails
  if (!tripDetails) {
    return {
      detailsArray1: [],
      detailsArray2: [],
      detailsArray3: [],
      detailsArray4: [],
      detailsArray5: [],
      detailsArray6: [],
    };
  }

  const guestPassengers = tripDetails?.additionalInfo?.passengersDetails?.guestPassengers || [];
  const payments = tripDetails?.Payments || [];
  const payment = payments[0] || {};
  const tripPricingDetails = payment?.tripPricingDetails || {};
  const rateConfig = tripPricingDetails?.rateConfig || {};
  const breakdown = tripPricingDetails?.breakdown || [];

  // Safe access for nested objects
  const garageCharge = breakdown.find((item) => item?.chargeType === 'garageCharge') || {};
  const garageBreakup = garageCharge?.meta?.breakup || [];

  // Create guest details entries
  const guestDetailsTable =
    guestPassengers.length > 0
      ? {
          title: 'Guest Details',
          tableHeaders: ['Guest Name', 'Phone Number'],
          tableData: guestPassengers.map((guest, index) => ({
            id: index + 1,
            name: guest?.name || '',
            phone: guest?.countryCode ? `${guest.countryCode} ${guest.phone}` : guest?.phone || '',
          })),
        }
      : null;

  return {
    detailsArray1: [
      {
        title: 'Service',
        value:
          (tripDetails?.dutyTypeName || '') +
          (tripDetails?.additionalInfo?.isMultiDay ? ' (Multi-Day)' : ''),
      },
      {
        title: 'Company Name',
        value: tripDetails?.companyName || '',
      },
      {
        title: 'Passenger',
        value: tripDetails?.customerName || '',
      },
      {
        title: 'Mobile No.',
        value: tripDetails?.customerPhone || '',
      },
      {
        title: 'Booked By',
        value: tripDetails?.TripBookedBy?.name || '',
      },
      {
        title: 'No. Of Guest',
        // Two quirks: `n - 1 || ''` rendered a legitimate 0 (solo traveller)
        // as blank, and the frontdesk booking path stores the count as
        // additionalInfo.passengers (not passengersDetails.numberOfPassengers).
        value: (() => {
          const count = Number(
            tripDetails?.additionalInfo?.passengersDetails?.numberOfPassengers ??
              tripDetails?.additionalInfo?.passengers,
          );
          return Number.isFinite(count) ? `${Math.max(0, count - 1)}` : '';
        })(),
      },
      ...(guestDetailsTable
        ? [
            {
              title: 'Guest Details',
              isTable: true,
              tableData: guestDetailsTable,
            },
          ]
        : []),
    ],
    detailsArray2: [
      {
        title: 'Submission City',
        value: tripDetails?.ServiceCity?.name || '',
      },
      {
        title: 'DS No. / Booking ID',
        value: tripDetails?.tripId || '',
      },
      {
        title: 'Reporting Address',
        value: tripDetails?.sourceAddress || '',
      },
      {
        title: 'Package',
        value:
          tripDetails?.type === tripDutyTypeEnum.rental ||
          tripDetails?.type === tripDutyTypeEnum.roundTrip
            ? rateConfig?.name
            : 'Per KM',
      },
      {
        title: 'Garage Location',
        value: tripDetails?.tripBranch?.additionalInfo?.garageAddress,
      },
    ],
    detailsArray3: [
      {
        title: 'Date Out',
        value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : '',
      },
      {
        title: 'Date In',
        value: tripDetails?.completedAt ? getDateFormat(tripDetails?.completedAt) : '',
      },
      {
        title: 'Total Days',
        value:
          payment?.tripPricingDetails?.rateConfig?.durationType === 'day'
            ? payment?.tripPricingDetails?.rateConfig?.duration
            : tripDetails?.pickUpTime && tripDetails?.completedAt
              ? Math.max(
                  1,
                  Math.ceil(
                    (new Date(tripDetails.completedAt) - new Date(tripDetails.pickUpTime)) /
                      86400000,
                  ),
                )
              : '',
      },
      {
        title: 'Time Out',
        value: getTimeFormat(tripDetails?.pickUpTime) || '',
      },
      {
        title: 'Time In',
        value: getTimeFormat(tripDetails?.completedAt) || '',
      },
      {
        title: 'Total hr',
        value:
          payment?.tripPricingDetails?.rateConfig?.durationType === 'day'
            ? payment?.tripPricingDetails?.rateConfig?.duration
            : convertDecimalHoursToTime(tripDetails?.totalTime) || '',
      },
      {
        title: 'km Out',
        value: `${tripDetails?.additionalInfo?.odometer?.start ?? 0}`,
      },
      {
        title: 'km In',
        value: `${tripDetails?.additionalInfo?.odometer?.end ?? tripDetails?.distanceActual ?? ''}`,
      },
      {
        title: 'Total km',
        value: `${tripDetails?.distanceActual || ''}`,
      },
    ],
    detailsArray4: [
      {
        title: 'Garage to Pickup',
        value: `${tripDetails?.additionalInfo?.kilometersOut ? tripDetails?.additionalInfo?.kilometersOut + ' km' : '0.00 km'}`,
      },
      {
        title: 'Pickup to Drop',
        value: `${tripDetails?.distanceActual ? tripDetails?.distanceActual + ' km' : '0.00 km'}`,
      },
      {
        title: 'Drop to Garage',
        value: `${tripDetails?.additionalInfo?.kilometersIn ? tripDetails?.additionalInfo?.kilometersIn + ' km' : '0.00 km'}`,
      },
      {
        // Deliberately the ₹/km RATE, not the evaluated amount: the duty slip
        // records facts (kms) and agreed rates; pricing happens on the invoice.
        title: 'Garage to Pickup Rate',
        value: `₹${formatNumber(
          parseFloat(
            garageBreakup.find((g) => g?.name === 'GarageToPickup')?.rate ??
              garageBreakup[0]?.rate ??
              0,
          ),
        )}/km`,
      },
      {
        title: 'Drop to Garage Rate',
        value: `₹${formatNumber(
          parseFloat(
            garageBreakup.find((g) => g?.name === 'DropToGarage')?.rate ??
              garageBreakup[1]?.rate ??
              0,
          ),
        )}/km`,
      },
    ],
    detailsArray5: [
      {
        title: 'Rep Time',
        value: getTimeFormat(tripDetails?.scheduledTime) || '',
      },
      {
        title: 'Driver',
        value: tripDetails?.DriverProfile?.driverName || '',
      },
      {
        title: 'Mobile No.',
        value: tripDetails?.DriverProfile?.mobile || '',
      },
      {
        title: 'Car Booked',
        value: tripDetails?.MasterCarType?.carType || '',
      },
      {
        title: 'Car Alloted',
        value: tripDetails?.DriverProfile?.Vehicle?.carType || '',
      },
      {
        title: 'Car No.',
        value: tripDetails?.DriverProfile?.Vehicle?.regNumber || '',
      },

      ...(tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName
        ? [
            {
              title: 'Sub Vendor',
              value: tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName,
            },
          ]
        : []),
    ],
    detailsArray6: [
      {
        title: 'Opening hr',
        value: getTimeFormat(tripDetails?.pickUpTime) || '',
      },
      {
        title: 'Closing hr',
        value: getTimeFormat(tripDetails?.completedAt) || '',
      },
      {
        title: 'Opening km',
        value: `${tripDetails?.additionalInfo?.odometer?.start ?? 0}`,
      },
      {
        title: 'Closing km',
        value: `${tripDetails?.additionalInfo?.odometer?.end ?? tripDetails?.distanceActual ?? ''}`,
      },
    ],
  };
};

export const tripPerformaInvoiceData = (
  tripDetails,
  tripTypeDetails,
  subTotalA,
  subTotalB,
  totalAB,
  totalGstAmount,
  grossTotal,
) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const breakdown = tripPricing?.breakdown || [];
  const extraKmCharges = getExtraKmCharge(tripDetails);
  const extraHrCharges = getExtraHrCharge(tripDetails);
  const extraKmEntry = breakdown.find((b) => b?.chargeType?.toLowerCase() === 'extrakm');
  const extraHrEntry = breakdown.find((b) => b?.chargeType?.toLowerCase() === 'extrahrs');
  const driverAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'driverAllowance')?.addedCharge || 0,
  );
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'nightAllowance')?.addedCharge || 0,
  );
  const fixedGarageRate = parseFloat(tripTypeDetails?.fixedGarageRate || 0);
  const garageCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'garageCharge')?.addedCharge || 0,
  );
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const tollCharges = Number(
    tripDetails?.Payments[0]?.tripPricingDetails?.breakdown?.find(
      (item) => item.chargeType === 'tollCharges',
    )?.addedCharge ?? 0,
  );
  const parkingCharges = Number(
    tripDetails?.Payments[0]?.tripPricingDetails?.breakdown?.find(
      (item) => item.chargeType === 'parkingCharge',
    )?.addedCharge ?? 0,
  );
  const additionalCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'additionalCharges')?.addedCharge || 0,
  );
  const placardCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'placard')?.addedCharge || 0,
  );
  const couponDiscount = Math.abs(
    parseFloat(breakdown.find((b) => b.chargeType === 'discountAmount')?.addedCharge || 0),
  );

  // Build detailsArray4 with only non-zero values
  const detailsArray4Base = [
    {
      title: 'Service Type',
      value:
        (tripDetails?.dutyTypeName || '') +
        (tripDetails?.additionalInfo?.isMultiDay ? ' (Multi-Day)' : ''),
    },
    {
      title: 'Package',
      value:
        tripDetails?.type === tripDutyTypeEnum.rental ||
        tripDetails?.type === tripDutyTypeEnum.roundTrip
          ? tripDetails?.Payments[0]?.tripPricingDetails?.rateConfig?.name
          : 'Per KM',
    },
    {
      title: 'Package Rate',
      value: `₹${formatNumber(tripDetails?.Payments[0]?.tripPricingDetails?.baseFareDisplay || 0)}`,
    },
    {
      title: 'km Used',
      value: `${formatNumber(parseFloat(tripDetails?.distanceActual || '0') + parseFloat(tripDetails?.extraKm || '0'))} `,
    },
    ...(extraKmCharges > 0
      ? [
          {
            title: 'Extra km',
            value: `${formatNumber(extraKmEntry?.meta?.extraKms?.roundedOff ?? tripDetails?.extraKm) || ''}`,
          },
          {
            title: 'Extra km Rs',
            value: `₹${formatNumber(extraKmCharges)}`,
          },
        ]
      : []),
    {
      title: 'hr Used',
      value: convertDecimalHoursToTime(tripDetails?.totalTime) || '',
    },
    ...(extraHrCharges > 0
      ? [
          {
            title: 'Extra hr',
            value: `${formatNumber(extraHrEntry?.meta?.extraHrs?.roundedOff ?? tripDetails?.extraTime) || ''}`,
          },
          {
            title: 'Extra hr Rs',
            value: `₹${formatNumber(extraHrCharges)}`,
          },
        ]
      : []),
    {
      title: 'Garage To Pickup',
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersOut || 'NA',
    },
    {
      title: 'Drop To Garage',
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersIn || 'NA',
    },
    {
      title: 'Car Booked',
      value: tripDetails?.MasterCarType?.carType || '',
    },
    {
      title: 'Car Used On',
      value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : '',
    },
    ...(baseRate > 0
      ? [
          {
            title: 'Base Rate',
            value: `₹${formatNumber(baseRate)}`,
          },
        ]
      : []),
    ...(surCharge > 0
      ? [
          {
            title: 'Surcharge',
            value: `₹${formatNumber(surCharge)}`,
          },
        ]
      : []),
    ...(platformCharge > 0
      ? [
          {
            title: 'Platform Charge',
            value: `₹${formatNumber(platformCharge)}`,
          },
        ]
      : []),
  ];

  // Sub Total A summary section
  const detailsArray4Summary = [
    {
      title: '',
      value: '',
    },
    {
      title: '',
      value: '',
    },
    {
      title: 'Sub Total',
      bold: true,
      value: `₹${formatNumber(parseFloat(calculateSubTotalA(tripDetails)) || 0)}` || '',
    },
  ];

  // Build detailsArray5 with only non-zero values
  const detailsArray5Base = [
    ...(tollCharges > 0 > 0
      ? [
          {
            title: 'Toll Charges',
            value: `₹${formatNumber(tollCharges)}`,
          },
        ]
      : []),
    ...(parkingCharges > 0
      ? [
          {
            title: 'Parking Charges',
            value: `₹${formatNumber(parkingCharges)}`,
          },
        ]
      : []),
    ...(driverAllowance > 0
      ? [
          {
            title: 'Driver Allowance',
            value: `₹${formatNumber(driverAllowance)}` || '',
          },
        ]
      : []),
    ...(handlingCharges > 0
      ? [
          {
            title: 'Additional Charges',
            value: `₹${formatNumber(handlingCharges)}` || '',
          },
        ]
      : []),
    ...(nightAllowance > 0
      ? [
          {
            title: 'Night Allowance',
            value: `₹${formatNumber(nightAllowance)}`,
          },
        ]
      : []),
    ...(fixedGarageRate > 0
      ? [
          {
            title: 'Fix Garage Run',
            value: `₹${formatNumber(fixedGarageRate)}`,
          },
        ]
      : []),
    ...(garageCharge > 0
      ? [
          {
            title: 'Garage To Garage Run',
            value: `₹${formatNumber(garageCharge)}`,
          },
        ]
      : []),
    ...(additionalCharges > 0
      ? [
          {
            title: 'Additional Charges',
            value: `₹${formatNumber(additionalCharges)}` || '',
          },
        ]
      : []),
    ...(placardCharge > 0
      ? [
          {
            title: 'Placard Charge',
            value: `₹${formatNumber(placardCharge)}`,
          },
        ]
      : []),
    ...(fuelSurcharge > 0
      ? [
          {
            title: 'Fuel Surcharge',
            value: `₹${formatNumber(fuelSurcharge)}` || '',
          },
        ]
      : []),
    ...(interstateTax > 0
      ? [
          {
            title: 'Interstate Tax',
            value: `₹${formatNumber(interstateTax)}`,
          },
        ]
      : []),
    ...(couponDiscount > 0
      ? [
          {
            title: 'Coupon Discount',
            value: `-₹${formatNumber(couponDiscount)}`,
          },
        ]
      : []),
  ];

  // Sub Total B summary section
  const detailsArray5Summary = [
    {
      title: '',
      value: '',
    },
    {
      title: '',
      value: '',
    },
    {
      title: 'Sub Total',
      bold: true,
      value: `₹${formatNumber(parseFloat(calculateSubTotalB(tripDetails) || 0))}` || '',
    },
  ];

  return {
    detailsArray1: [
      {
        title: 'Company Name',
        value: tripDetails?.companyName || '',
      },
      // {
      //   title: "Company Code",
      //   value: "",
      // },
      {
        title: 'Company Address',
        value: tripDetails?.tripCompany?.address || '',
      },
      {
        title: 'Passenger Name',
        value: tripDetails?.customerName || '',
      },
      {
        title: 'Booked By',
        value: tripDetails?.TripBookedBy?.name || '',
      },
      {
        title: 'GSTIN of Company',
        value: tripDetails?.tripBranch?.gstNumber || '',
      },
      {
        title: 'Travel Request No.',
        value: tripDetails?.travelRequestNumber || '',
      },
      {
        title: 'PAN',
        value: tripDetails?.tripCompany?.panNumber || '',
      },
    ],
    detailsArray2: [
      {
        title: 'Billing Type',
        value:
          Number(tripDetails?.additionalInfo?.billingType) === billingTypeEnum?.billToCreditCard
            ? 'Bill To Credit Card'
            : Number(tripDetails?.additionalInfo?.billingType) === billingTypeEnum?.billToCompany
              ? 'Bill To Company'
              : '',
      },
      {
        title: 'DS No. / Booking ID',
        value: tripDetails?.tripId || '',
      },
      {
        title: 'Invoice Date',
        value: getDateFormat(tripDetails?.Invoices?.[0]?.createdAt) || '',
      },
      {
        title: 'Submission City',
        value: tripDetails?.ServiceCity?.name || '',
      },
      {
        title: 'Place Of Supply',
        value: tripDetails?.tripBranch?.state || tripDetails?.ServiceCity?.name || '',
      },
      {
        title: 'Garage Location',
        value: tripDetails?.tripBranch?.additionalInfo?.garageAddress,
      },
    ],
    detailsArray3: [
      {
        title: 'Date Out',
        value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : '',
      },
      {
        title: 'Date In',
        value: tripDetails?.completedAt ? getDateFormat(tripDetails?.completedAt) : '',
      },
      {
        title: 'Start km',
        value: `${tripDetails?.additionalInfo?.odometer?.start ?? 0}`,
      },
      {
        title: 'Time Out',
        value: getTimeFormat(tripDetails?.pickUpTime) || '',
      },
      {
        title: 'Time In',
        value: getTimeFormat(tripDetails?.completedAt) || '',
      },
      {
        title: 'End km',
        value: `${tripDetails?.additionalInfo?.odometer?.end ?? tripDetails?.distanceActual ?? ''}`,
      },
      {
        title: 'Driver Name',
        value: tripDetails?.DriverProfile?.driverName || '',
      },
      {
        title: 'Driver Phone No.',
        value: tripDetails?.DriverProfile?.mobile || '',
      },
      {
        title: 'Reporting Time',
        value: getTimeFormat(tripDetails?.scheduledTime) || '',
      },
      {
        title: 'Car No.',
        value: tripDetails?.DriverProfile?.Vehicle?.regNumber || '',
      },
      {
        title: 'Car Alloted',
        value: tripDetails?.DriverProfile?.Vehicle?.carType || '',
      },
      ...(tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName
        ? [
            {
              title: 'Sub Vendor',
              value: tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName,
            },
          ]
        : []),
    ],
    detailsArray4: detailsArray4Base,
    detailsArray4Summary: detailsArray4Summary,
    detailsArray5: detailsArray5Base,
    detailsArray5Summary: detailsArray5Summary,
    detailsArray6: tripDetails?.termsAndConditions,
    detailsArray7: [
      {
        title: tripDetails?.status === 'Cancelled' ? 'Sub Total (C)' : 'Gross Amount(A+B)',
        bold: true,
        value: `₹${formatCurrency(parseFloat(totalAB))}`,
      },
      {
        title: `CGST Tax @${
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'cgst')?.unitValue || 0
        }%`,
        value: `₹${formatNumber(
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'cgst')?.totalCharge || 0,
        )}`,
      },
      {
        title: `SGST Tax @${
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'sgst')?.unitValue || 0
        }%`,
        value: `₹${formatNumber(
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'sgst')?.totalCharge || 0,
        )}`,
      },
      {
        title: 'Total Tax Amt',
        value: `₹${formatNumber(tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find((b) => b.chargeType === 'gst')?.addedCharge || 0)}`,
      },
      {
        title: 'Total Amount',
        bold: true,
        value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price))}`,
      },
    ],
    detailsArray8: [
      {
        title: 'Gross Amount(A+B)',
        bold: true,
        value: `₹${formatCurrency(parseFloat(calculateSubTotalA(tripDetails) + calculateSubTotalB(tripDetails)))}`,
      },
      {
        title: `CGST Tax @${tripDetails?.Payments?.[0]?.cgst}%`,
        value: `₹${formatNumber(parseFloat(parseFloat(totalAB) * 0.01 * parseFloat(tripDetails?.Payments?.[0]?.cgst)))}`,
      },
      {
        title: `SGST Tax @${tripDetails?.Payments?.[0]?.sgst}%`,
        value: `₹${formatNumber(parseFloat(totalAB) * 0.01 * parseFloat(tripDetails?.Payments?.[0]?.sgst))}`,
      },
      {
        title: 'Total Tax Amt',
        value: `₹${parseFloat(totalGstAmount)?.toFixed(2)}` || '',
      },
      {
        title: 'Total Amount',
        bold: true,
        value:
          `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price))}` ||
          '',
      },
    ],
  };
};

export const consolidateInvoiceData = (tripDetails, queryParams) => {
  const totalAmtVal = tripDetails?.totalAmount ?? tripDetails?.totalAmt ?? 0;
  const tdsVal = tripDetails?.tds ?? tripDetails?.tdsAmt ?? tripDetails?.tdsAmount ?? 0;
  const outstandingAmtVal = tripDetails?.outstandingAmt ?? tripDetails?.outstandingAmount ?? 0;

  const totalAmtNum = parseFloat(totalAmtVal) || 0;
  const tdsNum = parseFloat(tdsVal) || 0;
  const outstandingAmtNum = parseFloat(outstandingAmtVal) || 0;

  // Invoice Amount is the total (gross + GST). TDS is withheld by the payer,
  // so it reduces what is received, never what is invoiced; the backend's
  // outstanding is already total − TDS − paid, and paid is what it recorded.
  const invoiceAmt = totalAmtNum;
  const paidAmtVal = tripDetails?.amountSettled ?? tripDetails?.paidAmount;
  const paidAmt =
    paidAmtVal !== undefined && paidAmtVal !== null
      ? parseFloat(paidAmtVal) || 0
      : Math.max(0, invoiceAmt - tdsNum - outstandingAmtNum);

  const formatVal = (val) => {
    if (val === 0) return 0;
    if (!val) return '';
    const parsed = parseFloat(val);
    if (isNaN(parsed)) return val;
    return parsed % 1 === 0 ? parsed : parsed.toFixed(2);
  };

  return {
    companyAckDetails: [
      {
        title: 'IRN',
        value: tripDetails?.irn || '',
      },
      {
        title: 'Ack No.',
        value: tripDetails?.ackNo || '',
      },
      {
        title: 'Ack Date',
        value: tripDetails?.ackDate || '',
      },
    ],
    companyCinDetails: [
      {
        title: 'Submission City',
        value: tripDetails?.submissionCity || '',
      },
      {
        title: 'CIN',
        value: tripDetails?.cin || '',
      },
      {
        title: 'HSN/SAC Code',
        value: tripDetails?.hsnCode || '',
      },
      ...(tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName
        ? [
            {
              title: 'Sub Vendor',
              value: tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName,
            },
          ]
        : []),
    ],
    companyDetails: [
      {
        title: 'Gross Amount',
        value: tripDetails?.grossAmt ?? tripDetails?.grossAmount ?? '',
      },
      {
        title: 'Additional Amount',
        value: tripDetails?.additionalAmount ?? tripDetails?.additionalAmt ?? 0,
      },
      {
        title: 'GST Amount',
        value: tripDetails?.gstAmt ?? tripDetails?.gstAmount ?? '',
      },
      {
        title: 'Total Amount',
        value: tripDetails?.totalAmount ?? tripDetails?.totalAmt ?? '',
      },
      {
        title: 'TDS',
        value: tripDetails?.tds ?? tripDetails?.tdsAmt ?? tripDetails?.tdsAmount ?? '',
      },
      {
        title: 'Invoice Amount',
        value: formatVal(invoiceAmt),
      },
      {
        title: 'Paid Amount',
        value: formatVal(paidAmt),
      },
      {
        title: 'Outstanding Amount',
        value: tripDetails?.outstandingAmt ?? tripDetails?.outstandingAmount ?? '',
      },
    ],
    companyNameDetails: [
      {
        title: 'Company Name',
        value:
          tripDetails?.companyName ||
          tripDetails?.recipientCompanyName ||
          tripDetails?.clientCompanyName ||
          queryParams?.companyName ||
          '',
      },
      {
        title: 'Company Address',
        value:
          tripDetails?.companyAddress ||
          tripDetails?.recipientCompanyAddress ||
          tripDetails?.clientCompanyAddress ||
          '',
      },
    ],
    companyPanDetails: [
      {
        title: 'PAN',
        value: tripDetails?.companyPan || '',
      },
      {
        title: 'Company GST No.',
        value: tripDetails?.companyGst || '',
      },
      {
        title: 'POS',
        value: tripDetails?.pos || '',
      },
      {
        title: 'Company Type',
        value: tripDetails?.companyType || '',
      },
      {
        title: 'Duties  Form',
        value: tripDetails?.dutiesFrom || '',
      },
      {
        title: 'Duties  To',
        value: tripDetails?.dutiesTo || '',
      },
    ],
    companyInvoiceDetails: [
      {
        title: 'Invoice Date',
        value:
          tripDetails?.invoiceDate || tripDetails?.inoviceDate || tripDetails?.invoice_date || '',
      },
      {
        title: 'Invoice No.',
        value:
          tripDetails?.invoiceId ||
          tripDetails?.inoviceId ||
          tripDetails?.invoice_id ||
          tripDetails?.invoiceNo ||
          '',
      },
      {
        title: 'GST No.',
        value: tripDetails?.gstNo || '',
      },
      {
        title: 'PAN',
        value: tripDetails?.panNo || '',
      },
    ],
  };
};

export const tripIndividualInvoiceData = (
  tripDetails,
  tripTypeDetails,
  subTotalA,
  subTotalB,
  totalAB,
  totalGstAmount,
) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const extraKmCharges =
    tripDetails?.type === tripDutyTypeEnum?.rental
      ? parseFloat(tripDetails?.extraKmCharges || 0)
      : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrCharges = parseFloat(tripDetails?.extraHrCharges || 0);
  const breakdown = tripPricing?.breakdown || [];
  const tollCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'tollCharges')?.addedCharge || 0,
  );
  const parkingCharges = parseFloat(
    breakdown.find((b) => b.chargeType === 'parkingCharge')?.addedCharge || 0,
  );
  const driverAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'driverAllowance')?.addedCharge || 0,
  );
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(
    breakdown.find((b) => b.chargeType === 'nightAllowance')?.addedCharge || 0,
  );
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(
    breakdown.find((b) => b.chargeType === 'placard')?.addedCharge || 0,
  );
  const couponDiscount = Math.abs(
    parseFloat(breakdown.find((b) => b.chargeType === 'discountAmount')?.addedCharge || 0),
  );

  // Build detailsArray4 with only non-zero values
  const detailsArray4Base = [
    {
      title: 'Service Type',
      value:
        (tripDetails?.dutyTypeName || '') +
        (tripDetails?.additionalInfo?.isMultiDay ? ' (Multi-Day)' : ''),
    },
    {
      title: 'Package',
      value:
        tripDetails?.type === tripDutyTypeEnum.rental ||
        tripDetails?.type === tripDutyTypeEnum.roundTrip
          ? tripDetails?.Payments[0]?.tripPricingDetails?.rateConfig?.name
          : 'Per KM',
    },
    {
      title: 'Package Rate',
      value: `₹${formatNumber(tripDetails?.Payments[0]?.tripPricingDetails?.baseFareDisplay || 0)}`,
    },
    {
      title: 'km Used',
      value: `${formatNumber(parseFloat(tripDetails?.distanceActual || '0') + parseFloat(tripDetails?.extraKm || '0'))} `,
    },
    ...(parseFloat(tripDetails?.extraKm) > 0
      ? [
          {
            title: 'Extra km',
            value: `${formatNumber(tripDetails?.extraKm) || ''}`,
          },
          {
            title: 'Extra km Rs',
            value:
              tripDetails?.type === tripDutyTypeEnum?.rental
                ? `₹${formatNumber(tripDetails?.extraKmCharges)}`
                : `₹${formatNumber(tripDetails?.extraPrice)}`,
          },
        ]
      : []),
    {
      title: 'hr Used',
      value: convertDecimalHoursToTime(tripDetails?.totalTime) || '',
    },
    ...(parseFloat(tripDetails?.extraTime) > 0
      ? [
          {
            title: 'Extra hr',
            value: tripDetails?.extraTime ? parseFloat(tripDetails?.extraTime)?.toFixed(2) : '',
          },
          {
            title: 'Extra hr Rs',
            value:
              tripDetails?.type === tripDutyTypeEnum?.rental
                ? `₹${tripDetails?.extraHrCharges}`
                : '0',
          },
        ]
      : []),
    {
      title: 'Car Booked',
      value: tripDetails?.MasterCarType?.carType || '',
    },
    {
      title: 'Car Used On',
      value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : '',
    },
    {
      title: 'Garage To Pickup',
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersOut || 'NA',
    },
    {
      title: 'Drop To Garage',
      value: tripDetails?.Payments[0]?.tripPricingDetails?.kilometersIn || 'NA',
    },
    ...(baseRate > 0
      ? [
          {
            title: 'Base Rate',
            value: `₹${formatNumber(baseRate)}`,
          },
        ]
      : []),
    ...(surCharge > 0
      ? [
          {
            title: 'Surcharge',
            value: `₹${formatNumber(surCharge)}`,
          },
        ]
      : []),
    ...(platformCharge > 0
      ? [
          {
            title: 'Platform Charge',
            value: `₹${formatNumber(platformCharge)}`,
          },
        ]
      : []),
  ];

  // Sub Total A summary section
  const detailsArray4Summary = [
    {
      title: '',
      value: '',
    },
    {
      title: '',
      value: '',
    },
    {
      title: 'Sub Total',
      bold: true,
      value: `₹${formatCurrency(calculateSubTotalA(tripDetails) || 0)}` || '',
    },
  ];

  // Build detailsArray5 with only non-zero values
  const detailsArray5Base = [
    ...(tollCharges > 0
      ? [
          {
            title: 'Toll Charges',
            value: `₹${formatNumber(tollCharges)}`,
          },
        ]
      : []),
    ...(parkingCharges > 0
      ? [
          {
            title: 'Parking Charges',
            value: `₹${formatNumber(parkingCharges)}`,
          },
        ]
      : []),
    ...(driverAllowance > 0
      ? [
          {
            title: 'Driver Allowance',
            value: `₹${formatNumber(driverAllowance)}` || '',
          },
        ]
      : []),
    ...(handlingCharges > 0
      ? [
          {
            title: 'Additional Charges',
            value: `₹${formatNumber(handlingCharges)}` || '',
          },
        ]
      : []),
    // {
    //   title: "Discount",
    //   value: `₹0.00`,
    // },
    ...(nightAllowance > 0
      ? [
          {
            title: 'Night Allowance',
            value: `₹${formatNumber(nightAllowance)}`,
          },
        ]
      : []),
    ...(fuelSurcharge > 0
      ? [
          {
            title: 'Fuel Surcharge',
            value: `₹${formatNumber(fuelSurcharge)}` || '',
          },
        ]
      : []),
    ...(interstateTax > 0
      ? [
          {
            title: 'Interstate Tax',
            value: `₹${formatNumber(interstateTax)}`,
          },
        ]
      : []),
    ...(placardCharge > 0
      ? [
          {
            title: 'Placard Charge',
            value: `₹${formatNumber(placardCharge)}`,
          },
        ]
      : []),
    ...(getGarageCharges(tripDetails) > 0
      ? [
          {
            title: 'Garage to Pickup Charge',
            value: `₹${formatNumber(parseFloat(breakdown.find((b) => b.chargeType === 'garageCharge')?.meta?.breakup?.[0]?.rate || 0))}`,
          },
          {
            title: 'Drop to Garage Charge',
            value: `₹${formatNumber(parseFloat(breakdown.find((b) => b.chargeType === 'garageCharge')?.meta?.breakup?.[1]?.rate || 0))}`,
          },
        ]
      : []),
    ...(couponDiscount > 0
      ? [
          {
            title: 'Coupon Discount',
            value: `-₹${formatNumber(couponDiscount)}`,
          },
        ]
      : []),
  ];

  // Sub Total B summary section
  const detailsArray5Summary = [
    {
      title: '',
      value: '',
    },
    {
      title: '',
      value: '',
    },
    {
      title: 'Sub Total',
      bold: true,
      value: `₹${formatCurrency(parseFloat(calculateSubTotalB(tripDetails) || 0))}` || '',
    },
  ];

  return {
    companyAckDetails: [
      {
        title: 'IRN',
        value: '',
      },
      {
        title: 'Ack No.',
        value: '',
      },
      {
        title: 'Ack Date',
        value: '',
      },
    ],
    companyGstDetails1: [
      {
        title: 'GSTIN',
        value: tripDetails?.tripBranch?.gstNumber || '',
      },
      {
        title: 'PAN',
        value: tripDetails?.tripCompany?.panNumber || '',
      },
    ],

    companyDetails2: [
      // {
      //   title: "HSN/SAC Code",
      //   value: "",
      // },
      // {
      //   title: "Rental Type",
      //   value: "",
      // }
    ],
    detailsArray1: [
      {
        title: 'Company Name',
        value: tripDetails?.companyName || '',
      },
      // {
      //   title: "Company Code",
      //   value: "",
      // },
      {
        title: 'Company Address',
        value: tripDetails?.tripCompany?.address || '',
      },
      {
        title: 'Passenger Name',
        value: tripDetails?.customerName || '',
      },
      {
        title: 'Booked By',
        value: tripDetails?.TripBookedBy?.name || '',
      },
      {
        title: 'GSTIN of Company',
        value: tripDetails?.tripBranch?.gstNumber || tripDetails?.tripCompany?.gstNumber || '',
      },
      {
        title: 'Travel Request No.',
        value: tripDetails?.travelRequestNumber || '',
      },
      {
        title: 'PAN',
        value: tripDetails?.tripCompany?.panNumber || '',
      },
    ],
    detailsArray2: [
      {
        title: 'Billing Type',
        value:
          Number(tripDetails?.additionalInfo?.billingType) === billingTypeEnum?.billToCreditCard
            ? 'Bill To Credit Card'
            : Number(tripDetails?.additionalInfo?.billingType) === billingTypeEnum?.billToCompany
              ? 'Bill To Company'
              : '',
      },
      {
        title: 'DS No. / Booking ID',
        value: tripDetails?.tripId || '',
      },
      {
        title: 'Invoice Date',
        value: getDateFormat(tripDetails?.Invoices?.[0]?.createdAt) || '',
      },
      {
        title: 'Invoice No.',
        value: '',
      },
      {
        title: 'Submission City',
        value: tripDetails?.ServiceCity?.name || '',
      },

      {
        title: 'Place Of Supply',
        value: tripDetails?.tripBranch?.state || tripDetails?.ServiceCity?.name || '',
      },
    ],
    detailsArray3: [
      {
        title: 'Date Out',
        value: tripDetails?.pickUpTime ? getDateFormat(tripDetails?.pickUpTime) : '',
      },
      {
        title: 'Date In',
        value: tripDetails?.completedAt ? getDateFormat(tripDetails?.completedAt) : '',
      },
      {
        title: 'Start km',
        value: `${tripDetails?.additionalInfo?.odometer?.start ?? 0}`,
      },
      {
        title: 'Time Out',
        value: getTimeFormat(tripDetails?.pickUpTime) || '',
      },
      {
        title: 'Time In',
        value: getTimeFormat(tripDetails?.completedAt) || '',
      },
      {
        title: 'End km',
        value: `${tripDetails?.additionalInfo?.odometer?.end ?? tripDetails?.distanceActual ?? ''}`,
      },
      {
        title: 'Driver Name',
        value: tripDetails?.DriverProfile?.driverName || '',
      },
      {
        title: 'Driver Phone No.',
        value: tripDetails?.DriverProfile?.mobile || '',
      },
      {
        title: 'Reporting Time',
        value: getTimeFormat(tripDetails?.scheduledTime) || '',
      },
      {
        title: 'Car No.',
        value: tripDetails?.DriverProfile?.Vehicle?.regNumber || '',
      },
      ...(tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName
        ? [
            {
              title: 'Sub Vendor',
              value: tripDetails?.subVendorCompanyName || tripDetails?.DriverProfile?.subVendorName,
            },
          ]
        : []),
      {
        title: 'Car Alloted',
        value: tripDetails?.DriverProfile?.Vehicle?.carType || '',
      },
    ],
    detailsArray4: detailsArray4Base,
    detailsArray4Summary: detailsArray4Summary,
    detailsArray5: detailsArray5Base,
    detailsArray5Summary: detailsArray5Summary,
    detailsArray6: tripDetails?.termsAndConditions,
    detailsArray7: [
      {
        title: 'Gross Amount(A+B)',
        bold: true,
        value: `₹${formatCurrency(parseFloat(calculateSubTotalA(tripDetails) + calculateSubTotalB(tripDetails)))}`,
      },
      {
        title: `CGST Tax @${formatNumber(
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'cgst')?.unitValue || 0,
        )}%`,
        value: `₹${formatNumber(
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'cgst')?.totalCharge || 0,
        )}`,
      },
      {
        title: `SGST Tax @${
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'sgst')?.unitValue || 0
        }%`,
        value: `₹${
          tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown
            ?.find((b) => b.chargeType === 'gst')
            ?.meta?.breakup?.find((t) => t.name === 'sgst')?.totalCharge || 0
        }`,
      },
      {
        title: 'Total Tax Amt',
        value: `₹${tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find((b) => b.chargeType === 'gst')?.addedCharge || ''}`,
      },
      {
        title: 'Total Amount',
        bold: true,
        value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price))}`,
      },
    ],
    detailsArray8: [
      {
        title: 'Gross Amount(A+B)',
        bold: true,
        value: `₹${formatCurrency(parseFloat(totalAB))}`,
      },
      {
        title: `CGST Tax @${tripDetails?.Payments?.[0]?.cgst}%`,
        value: `₹${formatNumber(parseFloat(parseFloat(totalAB) * 0.01 * parseFloat(tripDetails?.Payments?.[0]?.cgst)) || 0)}`,
      },
      {
        title: `SGST Tax @${tripDetails?.Payments?.[0]?.sgst}%`,
        value: `₹${formatNumber(parseFloat(totalAB) * 0.01 * parseFloat(tripDetails?.Payments?.[0]?.sgst)) || 0}`,
      },
      {
        title: 'Total Tax Amt',
        value: `₹${formatNumber(parseFloat(totalGstAmount) || 0)}` || '',
      },
      {
        title: 'Total Amount',
        bold: true,
        value: `₹${formatCurrency(parseFloat(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount || tripDetails?.price) || 0)}`,
      },
    ],
  };
};

export const billSelfConsolidateInvoiceData = (invoiceDetails, cityName = '') => {
  const invoice = invoiceDetails?.header || {};
  const employee = invoice?.RecipientEmployee || {};

  const totalAmtVal = invoice?.totalAmount ?? 0;
  const tdsVal = invoice?.tds ?? 0;
  const outstandingAmtVal = invoice?.outstandingAmt ?? invoice?.outstandingAmount ?? 0;

  const totalAmtNum = parseFloat(totalAmtVal) || 0;
  const tdsNum = parseFloat(tdsVal) || 0;
  const outstandingAmtNum = parseFloat(outstandingAmtVal) || 0;

  // Same rule as the company consolidated view: Invoice Amount is the total;
  // TDS (0 for bill-self) reduces the receivable, not the invoice.
  const invoiceAmt = totalAmtNum;
  const paidAmtVal = invoice?.amountSettled ?? invoice?.paidAmount;
  const paidAmt =
    paidAmtVal !== undefined && paidAmtVal !== null
      ? parseFloat(paidAmtVal) || 0
      : Math.max(0, invoiceAmt - tdsNum - outstandingAmtNum);

  const formatVal = (val) => {
    if (val === 0) return 0;
    if (!val) return '';
    const parsed = parseFloat(val);
    if (isNaN(parsed)) return val;
    return parsed % 1 === 0 ? parsed : parsed.toFixed(2);
  };

  return {
    companyAckDetails: [
      {
        title: 'Employee Name',
        value: employee?.name || '',
      },
      {
        title: 'Employee Email',
        value: employee?.email || '',
      },
      {
        title: 'Employee Phone',
        value: `${employee?.countryCode || ''} ${employee?.phoneNumber || ''}`.trim(),
      },
    ],

    companyCinDetails: [
      {
        title: 'Payment Status',
        value: invoice?.paymentStatus || '',
      },
      {
        title: 'City',
        value: cityName || invoice?.cityName || '',
      },
    ],

    companyDetails: [
      {
        title: 'Gross Amount',
        value: invoice?.grossAmt ?? '',
      },
      {
        title: 'Additional Amount',
        value: invoice?.additionalAmount ?? 0,
      },
      {
        title: 'GST Amount',
        value: invoice?.gstAmt ?? '',
      },
      {
        title: 'Total Amount',
        value: invoice?.totalAmount ?? '',
      },
      {
        title: 'TDS',
        value: invoice?.tds ?? '',
      },
      {
        title: 'Invoice Amount',
        value: formatVal(invoiceAmt),
      },
      {
        title: 'Paid Amount',
        value: formatVal(paidAmt),
      },
      {
        title: 'Outstanding Amount',
        value: invoice?.outstandingAmt ?? '',
      },
    ],

    companyPanDetails: [],

    companyInvoiceDetails: [
      {
        title: 'Invoice Date',
        value: invoice?.createdAt
          ? invoice.createdAt.slice(0, 10).split('-').reverse().join('/')
          : '',
      },
      {
        title: 'Invoice No.',
        value: invoice?.invoiceId || '',
      },
    ],
  };
};
